<template>
  <div class="custom-scale-bar">
    <div class="scale-bar-container">
      <div class="scale-label">{{ formattedValue }}</div>
      <div class="scale-bar" :style="{ width: scaleWidth + 'px' }">
        <div class="scale-bar-black" :style="{ width: '50%' }"></div>
        <div class="scale-bar-white" :style="{ width: '50%' }"></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onBeforeUnmount } from 'vue'
import { MapScaleHelper } from '../../utils/helpers/MapScaleHelper'
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref'
import mapboxgl from 'mapbox-gl'

interface ScaleData {
  value: number
  unit: string
  pixels: number
}

export default defineComponent({
  name: 'ScaleBar',
  props: {
    map: {
      type: Object as () => mapboxgl.Map | null,
      default: null
    },
    latitude: {
      type: Number,
      default: 0
    },
    containerWidth: {
      type: Number,
      default: 150
    }
  },
  setup(props) {
    const scaleValue = ref(0)
    const unit = ref('m')
    const scaleWidth = ref(0)
    const formattedValue = ref('')

    const updateScale = () => {
      if (!props.map) return

      const zoom = props.map.getZoom()
      const scaleData: ScaleData = MapScaleHelper.getScaleDistance(zoom, props.containerWidth, props.latitude)

      // Get user's measurement preference
      const measurementPref = MeasurementPrefUtility.preference()

      // Convert distance to user's preferred units
      let displayValue = scaleData.value
      let displayUnit = scaleData.unit

      if (scaleData.unit === 'km') {
        // Convert km to user's preferred big unit
        displayValue = measurementPref.bigUnitValue(scaleData.value, false)
        displayUnit = measurementPref.kilometerSymbol()
      } else if (scaleData.unit === 'm') {
        // Convert m to user's preferred small unit
        displayValue = measurementPref.smallUnitValue(scaleData.value, false)
        displayUnit = measurementPref.meterSymbol()
      }

      scaleValue.value = displayValue
      unit.value = displayUnit
      scaleWidth.value = scaleData.pixels

      // Format the display value
      if (scaleData.unit === 'km') {
        formattedValue.value = displayValue >= 10 ? `${Math.round(displayValue)} ${displayUnit}` : `${displayValue.toFixed(1)} ${displayUnit}`
      } else {
        formattedValue.value = `${Math.round(displayValue)} ${displayUnit}`
      }
    }

    const onMapMove = () => {
      updateScale()
    }

    onMounted(() => {
      if (props.map) {
        props.map.on('move', onMapMove)
        props.map.on('zoom', onMapMove)
        updateScale()
      }
    })

    onBeforeUnmount(() => {
      if (props.map) {
        props.map.off('move', onMapMove)
        props.map.off('zoom', onMapMove)
      }
    })

    return {
      scaleValue,
      unit,
      scaleWidth,
      formattedValue
    }
  }
})
</script>

<style scoped>
.custom-scale-bar {
  border-radius: 3px;
  font-size: 11px;
  line-height: 1;
  z-index: 1001;
}

.scale-bar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 60px;
}

.scale-bar {
  display: flex;
  height: 8px;
  border: 1px solid #000;
  border-radius: 1px;
  overflow: hidden;
  margin-bottom: 2px;
}

.scale-bar-black {
  background-color: #000;
  height: 100%;
}

.scale-bar-white {
  background-color: #fff;
  height: 100%;
  border-left: 1px solid #000;
}

.scale-label {
  font-weight: 600;
  color: #000;
  font-size: 10px;
  white-space: nowrap;
}
</style>